import { useState, type FormEvent } from 'react';
import { api } from '../../api';
import { lerCoordenadas } from '../../componentes/formatos';

export interface DadosLocal {
  id?: string; nome: string; logradouro: string | null; numero: string | null; complemento: string | null; bairro: string | null;
  cidade: string | null; uf: string | null; cep: string | null; latitude: number | null; longitude: number | null;
  raio_geofence_m: number; instrucoes_acesso: string | null; ativo: boolean; versao?: number;
}

const VAZIO: DadosLocal = { nome: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '', cep: '', latitude: null, longitude: null, raio_geofence_m: 150, instrucoes_acesso: '', ativo: true };

export function FormLocal({ clienteId, inicial, aoSalvar, aoCancelar }: { clienteId: string; inicial?: DadosLocal; aoSalvar: () => void; aoCancelar: () => void }) {
  const [f, setF] = useState<DadosLocal>(inicial ?? VAZIO);
  const [coord, setCoord] = useState(inicial?.latitude != null ? `${inicial.latitude}, ${inicial.longitude}` : '');
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const set = (k: keyof DadosLocal) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  async function buscarCep() {
    const cep = (f.cep ?? '').replace(/\D/g, '');
    if (cep.length !== 8) return;
    setAviso(null);
    try {
      const r = await api<{ logradouro: string | null; bairro: string | null; cidade: string | null; uf: string | null }>(`/cep/${cep}`);
      setF((x) => ({ ...x, cep, logradouro: r.logradouro ?? x.logradouro, bairro: r.bairro ?? x.bairro, cidade: r.cidade ?? x.cidade, uf: r.uf ?? x.uf }));
    } catch (err) { setAviso(err instanceof Error ? err.message : null); }
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    let geo: { latitude: number | null; longitude: number | null } = { latitude: null, longitude: null };
    if (coord.trim()) {
      const c = lerCoordenadas(coord);
      if (!c) { setErro('Coordenadas inválidas. Cole como aparece no Google Maps, ex.: -22.7253, -47.6476'); return; }
      geo = c;
    }
    setSalvando(true);
    const corpo = {
      nome: f.nome, logradouro: f.logradouro || null, numero: f.numero || null, complemento: f.complemento || null, bairro: f.bairro || null,
      cidade: f.cidade || null, uf: (f.uf || '').toUpperCase() || null, cep: (f.cep ?? '').replace(/\D/g, '') || null,
      ...geo, raio_geofence_m: Number(f.raio_geofence_m) || 150, instrucoes_acesso: f.instrucoes_acesso || null, ativo: f.ativo,
    };
    try {
      if (inicial?.id) await api(`/locais/${inicial.id}`, { metodo: 'PATCH', corpo: { ...corpo, versao: inicial.versao } });
      else await api(`/clientes/${clienteId}/locais`, { metodo: 'POST', corpo });
      aoSalvar();
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível salvar.'); }
    finally { setSalvando(false); }
  }

  return (
    <form className="form" onSubmit={enviar}>
      <label>Nome do parque
        <input required maxLength={120} value={f.nome} onChange={set('nome')} placeholder="Ex.: Portaria P1, Portaria P2, Administração" autoFocus />
      </label>
      <div className="linha-cep">
        <label>CEP
          <input value={f.cep ?? ''} onChange={set('cep')} onBlur={buscarCep} inputMode="numeric" maxLength={9} placeholder="00000-000" />
        </label>
        <label>Logradouro<input maxLength={200} value={f.logradouro ?? ''} onChange={set('logradouro')} /></label>
      </div>
      <div className="linha-3">
        <label>Número<input maxLength={20} value={f.numero ?? ''} onChange={set('numero')} /></label>
        <label>Complemento<input maxLength={120} value={f.complemento ?? ''} onChange={set('complemento')} /></label>
        <label>Bairro<input maxLength={120} value={f.bairro ?? ''} onChange={set('bairro')} /></label>
      </div>
      <div className="linha-cidade">
        <label>Cidade<input maxLength={120} value={f.cidade ?? ''} onChange={set('cidade')} /></label>
        <label>UF<input maxLength={2} value={f.uf ?? ''} onChange={set('uf')} /></label>
      </div>
      {aviso && <small className="ajuda">{aviso}</small>}
      <div className="linha-2">
        <label>Coordenadas (portaria)
          <input value={coord} onChange={(e) => setCoord(e.target.value)} placeholder="-22.7253, -47.6476" />
          <small className="ajuda">No Google Maps: clique com o botão direito no portão e copie os números.</small>
        </label>
        <label>Raio do check-in (m)
          <input type="number" min={20} max={2000} value={f.raio_geofence_m} onChange={set('raio_geofence_m')} />
          <small className="ajuda">Distância máxima para o check-in do técnico contar como "no local".</small>
        </label>
      </div>
      <label>Instruções de acesso
        <textarea rows={2} maxLength={2000} value={f.instrucoes_acesso ?? ''} onChange={set('instrucoes_acesso')} placeholder="Ex.: falar com a ronda da guarita 2; chave do quadro com o zelador" />
      </label>
      {inicial?.id && (
        <label className="check"><input type="checkbox" checked={f.ativo} onChange={(e) => setF({ ...f, ativo: e.target.checked })} /> Parque em uso</label>
      )}
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Cancelar</button>
        <button className="btn" disabled={salvando || !f.nome.trim()}>{salvando ? 'Salvando…' : 'Salvar'}</button>
      </div>
    </form>
  );
}
